import React, { useCallback, useLayoutEffect } from 'react';
import { ScrollView, View } from 'react-native';
import Choices from 'design-system/Choices';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { employmentStatusMap, employmentStatuses } from 'features/borrow/monevo/models';
import { OnboardingFlow } from 'utils/types/navigation';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
import InvestHeaderWithHelpButton from '../components/InvestHeaderWithHelpButton';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { selectAnnualIncome, selectFundingSource, selectNetworth } from '../reducers/selectors';
const CHOICES = employmentStatuses;
const EmploymentStatusScreen = ({ route: { params }, navigation: { setOptions, popToTop }, }) => {
    const colors = useColors();
    const commonStyles = useCommonInvestStyles(params?.mountedOnAnotherScreen);
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenFromPots = useNavigateToNextScreenForPotsOnboarding();
    const networth = useAppSelector(selectNetworth);
    const annualIncomes = useAppSelector(selectAnnualIncome);
    const fundingSource = useAppSelector(selectFundingSource);
    useLayoutEffect(() => {
        setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => (<HelpScreenButton articlesKey={params?.flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>),
        });
    }, [colors.text.primary, params?.flow, setOptions]);
    useAmplitudeOnMountEvent(params?.flow, !!params?.mountedOnAnotherScreen, 'User Employment Screen');
    const dispatch = useAppDispatch();
    const onSubmit = useCallback((employmentStatusKey) => {
        const employmentStatus = employmentStatusMap[employmentStatusKey];
        if (annualIncomes && networth && fundingSource) {
            const newParams = {
                annualIncomeMin: annualIncomes.annualIncomeMin,
                annualIncomeMax: annualIncomes.annualIncomeMax,
                liquidNetWorthMin: networth.liquidNetWorthMin,
                liquidNetWorthMax: networth.liquidNetWorthMax,
                fundingSource,
                employmentStatus,
            };
            dispatch({
                type: 'SAVE_ONBOARDING_STATE',
                screen: 'InvestBrokerRequirements',
                params: newParams,
            });
            if (params?.flow === OnboardingFlow.Pots || params?.flow === OnboardingFlow.Wallet) {
                navigateToNextScreenFromPots();
            }
            else {
                navigateToNextScreen();
            }
        }
        else {
            Sentry.logError({
                filename: 'InvestEmploymentStatus',
                message: 'User not able to go ahead',
            });
            popToTop();
        }
    }, [annualIncomes, fundingSource, navigateToNextScreen, navigateToNextScreenFromPots, networth, params?.flow]);
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <InvestHeaderWithHelpButton hideButton={!params?.mountedOnAnotherScreen} isPotsOnb={params?.flow === OnboardingFlow.Pots} leftContent={params?.mountedOnAnotherScreen ? <LeaveOnboardingButton flow={params.flow}/> : undefined}/>
      <ScrollView scrollEnabled={false} keyboardShouldPersistTaps="handled" contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title={onboardingTitleStrings.Employment.title} subTitle={onboardingTitleStrings.Employment.subtitle}/>
        <View style={commonStyles.withPadding}>
          <Choices data={CHOICES} onSelect={onSubmit}/>
        </View>
      </ScrollView>
    </View>);
};
export default EmploymentStatusScreen;
